import Phaser from 'phaser';
import { createLocalStateStore, type LocalStateStore } from '@/lib/local-state';
import { getSharedUiPrefs } from '@/lib/shared-prefs';
import { decideStartScene } from '@/lib/scene-routing';
import { puzzleDateBudapest } from '@/lib/timezone';
import { dailyArchetype } from '@/lib/keeper';
import { gameBus } from '@/bridge/gameBus';
import { puzzleNumber } from '@/lib/launch';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Phaser owns the in-game ready/dive textures; the card hero zone is pure DOM.
    this.load.image('keeper-ready', '/assets/keepers/ready.png');
    this.load.image('keeper-dive-LF', '/assets/keepers/dive-LF.png');
    this.load.image('keeper-dive-LA', '/assets/keepers/dive-LA.png');
    this.load.image('keeper-dive-CF', '/assets/keepers/dive-CF.png');
    this.load.image('keeper-dive-CA', '/assets/keepers/dive-CA.png');
    this.load.image('keeper-dive-RF', '/assets/keepers/dive-RF.png');
    this.load.image('keeper-dive-RA', '/assets/keepers/dive-RA.png');
  }

  create(): void {
    const { width, height } = this.scale;
    this.add
      .text(width / 2, height / 2, 'Napi 11-es loading...', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5);

    const store: LocalStateStore = createLocalStateStore(window.localStorage);
    const today = this.resolveToday();

    this.registry.set('store', store);
    this.registry.set('today', today);
    // Spec §3.3: re-register the single canonical UiPrefs (created in main.tsx)
    // so PlayScene/MenuScene read it via registry.get('uiPrefs').
    this.registry.set('uiPrefs', getSharedUiPrefs());

    const state = store.getState();
    const record = state.history[today];
    const next = decideStartScene(state, today);

    const num = puzzleNumber(today);
    gameBus.emit({
      type: 'scene:enter',
      payload: { scene: 'BOOT', date: today, puzzleNumber: num },
    });

    switch (next) {
      case 'MenuScene':
        void dailyArchetype(today)
          .then((archetype) => {
            this.scene.launch('MenuScene', { archetype });
            this.scene.sleep('BootScene');
          })
          .catch((err) => {
            console.error('[BootScene] archetype fetch for MenuScene failed', err);
            // BootScene's "Napi 11-es loading..." text remains visible; user retries via reload.
          });
        break;
      case 'PlayScene':
        if (!record) throw new Error('routing returned PlayScene but no record exists');
        void dailyArchetype(today).then((archetype) => {
          this.scene.launch('PlayScene', {
            date: today,
            archetype,
            existingShots: record.shots,
            mode: record.mode ?? 'daily', // Plan #1 §4.1: reconstruct hard mode on resume
          });
          this.scene.sleep('BootScene');
        });
        break;
      case 'ResultScene':
        if (!record) throw new Error('routing returned ResultScene but no record exists');
        this.scene.launch('ResultScene', {
          date: today,
          record,
          streak: state.streak,
        });
        this.scene.sleep('BootScene');
        break;
    }
  }

  /**
   * Returns today's puzzle date. In DEV builds, an e2e test may inject
   * `window.__napiTestToday__` to simulate a different date.
   */
  private resolveToday(): string {
    if (import.meta.env.DEV && typeof window.__napiTestToday__ === 'string') {
      return window.__napiTestToday__;
    }
    return puzzleDateBudapest(new Date());
  }
}
